import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tag, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { xhrJson } from "../../utils/xhrJson";

const PALETTE = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#6366F1"];

export default function AdminRecipeTags() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/recipe-tags`, { credentials: "include" });
      setRows(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!editing?.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const isNew = !editing.recipe_tag_id;
      const url = isNew ? `${API}/admin/recipe-tags` : `${API}/admin/recipe-tags/${editing.recipe_tag_id}`;
      const { ok, data } = await xhrJson(isNew ? "POST" : "PUT", url, editing);
      if (!ok) throw new Error(data.detail || "Save failed");
      toast.success(isNew ? "Tag created" : "Tag updated");
      setEditing(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tag?")) return;
    try {
      const { ok, data } = await xhrJson("DELETE", `${API}/admin/recipe-tags/${id}`);
      if (!ok) throw new Error(data.detail || "Delete failed");
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <AdminLayout activeNav="recipe-tags">
      <div className="p-4 sm:p-6 space-y-4" data-testid="admin-recipe-tags">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><Tag size={18} /></div>
            <div>
              <h1 className="text-lg sm:text-xl font-extrabold text-gray-900">Recipe Tags</h1>
              <p className="text-xs text-gray-500">Pills used by nutritionists when classifying recipes.</p>
            </div>
          </div>
          <Button onClick={() => setEditing({ name: "", color_hex: PALETTE[0] })} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="rtag-new-btn">
            <Plus size={14} className="mr-1" /> New tag
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline" size={16} /></div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No tags yet. Create the first one above.</div>
          ) : (
            <div className="p-4 flex flex-wrap gap-2">
              {rows.map((t) => (
                <div key={t.recipe_tag_id} className="group inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full text-xs font-semibold" style={{ backgroundColor: `${t.color_hex}20`, color: t.color_hex }} data-testid={`rtag-row-${t.recipe_tag_id}`}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color_hex }} />
                  {t.name}
                  <button onClick={() => setEditing(t)} className="opacity-50 hover:opacity-100 ml-1" data-testid={`rtag-edit-${t.recipe_tag_id}`}><Pencil size={11} /></button>
                  <button onClick={() => handleDelete(t.recipe_tag_id)} className="opacity-50 hover:opacity-100" data-testid={`rtag-delete-${t.recipe_tag_id}`}><Trash2 size={11} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing?.recipe_tag_id ? "Edit tag" : "New tag"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} data-testid="rtag-form-name" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Color</Label>
                <div className="flex gap-2 flex-wrap">
                  {PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEditing((p) => ({ ...p, color_hex: c }))}
                      className={`w-7 h-7 rounded-full border-2 ${editing.color_hex === c ? "border-gray-900 scale-110" : "border-transparent"}`}
                      style={{ backgroundColor: c }}
                      data-testid={`rtag-color-${c.slice(1)}`}
                    />
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded p-3 flex items-center justify-center">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${editing.color_hex}20`, color: editing.color_hex }}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: editing.color_hex }} />
                  {editing.name || "preview"}
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="rtag-form-save">
              {saving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
